<template>
    <!-- edit user modal -->
    <Modal :title="$t('management.accounts.edit.modal.title')"
           @close="closeModal"
           :close-on-click-outside="false"
    >
        <template v-slot:body>
            <div class="flex flex_column modal-content">
                <md-field>
                    <label>{{ $t("common.label.name") }}</label>
                    <md-input v-model="form.tag"/>
                </md-field>
                <md-field :class="[{ 'md-error': errors.iban }]" :displayOnly="!canEditBaseInfo">
                    <label>{{ $t("common.iban") }}</label>
                    <md-input v-model="form.iban" required
                              @focus="clearError('iban')"
                              :disabled="!canEditBaseInfo"
                    />
                    <span v-if="errors.iban" class="validation-error">{{ errors.iban }}</span>
                </md-field>
                <md-field :class="[{ 'md-error': errors.bic }]" :displayOnly="!canEditBaseInfo">
                    <label>{{ $t("common.bic") }}</label>
                    <md-input v-model="form.bic" required
                              @focus="clearError('bic')"
                              :disabled="!canEditBaseInfo"
                    />
                    <span v-if="errors.bic" class="validation-error">{{ errors.bic }}</span>
                </md-field>
                <md-field :class="[{ 'md-error': errors.currency }]" :displayOnly="!canEditBaseInfo">
                    <label>{{ $t("common.label.currency") }}</label>
                    <md-input v-model="form.currency" required
                              @focus="clearError('currency')"
                              :disabled="!canEditBaseInfo"
                    />
                    <span v-if="errors.currency" class="validation-error">{{ errors.currency }}</span>
                </md-field>
                <div class="flex flex_items_center">
                    <md-checkbox v-model="form.buyAccount" :disabled="isVirtual">
                        {{ $t("common.label.portfolioAccount") }}
                    </md-checkbox>
                    <el-tooltip :open-delay="300" placement="right"
                                :content="$t('common.label.portfolioAccount.hint')">
                        <i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
                    </el-tooltip>
                    <label></label>
                </div>
                <template v-if="hasFullEbicsPermissions && !isVirtual">
                    <md-field displayOnly>
                        <label>{{ $t("common.label.accountBalance") }}</label>
                        <md-input disabled v-model="form.amount"/>
                    </md-field>
                    <md-field class="flex flex_items_center" displayOnly>
                        <label>{{ $t("common.label.reservedBalance") }}</label>
                        <md-input disabled v-model="form.reservedAmount"/>
                        <el-tooltip :open-delay="300" placement="right"
                                    :content="$t('common.label.reservedBalance.hint')">
                            <i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
                        </el-tooltip>
                    </md-field>
                </template>
            </div>
            <div class="flex flex_between margin_top">
                <md-button class="md-blank" @click="closeModal">
                    {{ $t("common.button.cancel") }}
                </md-button>
                <md-button class="md-primary" @click="editAccount">
                    {{ $t("common.button.submit") }}
                </md-button>
            </div>
        </template>
    </Modal>
</template>

<script>
import Modal from "@/components/templates/modal/Modal";

import {accountManagementApi} from "@/common/api/accountManagementApi";
import {enumDef} from "@/common/enumDefs";
import {formatAsMoneyAmount} from "@/common/format";

export default {
    props: {
        accountData: { type: Object, default: null }
    },
    components: {
        Modal
    },
    data() {
        return {
            form: {
                tag: "",
                iban: "",
                bic: "",
                currency: "",
                buyAccount: false,
                amount: 0,
                reservedAmount: 0,
            },
            errors: {
                iban: "",
                bic: "",
                currency: ""
            }
        };
    },
    computed: {
		isVirtual() {
			return !this.accountData.isVirtual;
		},
        canEditBaseInfo() {
            return this.accountData.status === enumDef.accountStatus.Created && !this.isVirtual;
        },
        hasFullEbicsPermissions() {
            return this.accountData.status === enumDef.accountStatus.CanReadAndWrite;
        }
    },
    beforeMount() {
        if (!this.accountData || !this.accountData.id) {
            return;
        }
        this.getAccountDetails();
    },
    methods: {
        closeModal() {
            this.$emit("close");
        },
        getAccountDetails() {
            accountManagementApi.inspection.getAccountInfo(this.accountData.id)
                .then(res => {
                    this.form = {
                        ...res,
                        amount: formatAsMoneyAmount(res.amount),
                        reservedAmount: formatAsMoneyAmount(res.reservedAmount),
                    };
                })
                .catch(e => this.toast(e));
        },
        validate() {
            let isValid = true;

            this.errors.iban = "";
            if (!this.form.iban) {
                this.errors.iban = this.$t("common.validation.error.inputRequired");
                isValid = false;
            }

            this.errors.bic = "";
            if (!this.form.bic) {
                this.errors.bic = this.$t("common.validation.error.inputRequired");
                isValid = false;
            }

            this.errors.currency = "";
            if (!this.form.currency) {
                this.errors.crrency = this.$t("common.validation.error.inputRequired");
                isValid = false;
            }

            return isValid;
        },
        clearError(key) {
            this.errors[key] = "";
        },
        editAccount() {
            if (!this.validate()) {
                return;
            }

            let data = {
                tag: this.form.tag,
                iban: this.form.iban,
                bic: this.form.bic,
                currency: this.form.currency,
                buyAccount: this.form.buyAccount
            };
            accountManagementApi.operation.editAccount(this.accountData.id, data)
                .then(() => {
                    this.$emit("changed");
                    this.toastSuccess();
                })
            .catch(e => this.toast(e));
        },
    }
};
</script>